/* eslint import/no-cycle: "off" */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from '../static';
import Header from './Header/Header';
import Headroom from 'react-headroom';
import { ApiIncludedService, ApiPackageModel } from '@ibe/api';
import { BreadcrumbProps } from '../Components/MMB/NavigationBreadcrumb/NavigationBreadcrumb';
import Footer from './Footer/Footer';
import useQuery from '../Util/useQuery';
import {
  b2bPages,
  extrasWorkflowPages,
  innerManageBookingPages,
  PageUrl,
  singleHotelBookingPages,
  vouchersWorkflowPages
} from '../pages';
import { WorkflowType } from '../Models/WorkflowType';
import useCms from '../Hooks/useCms';
import { QueryUtils } from '../Util/QueryUtils';

export interface LocationSnapshot {
  pathname: string;
  search: string;
}
interface Props {
  children: React.ReactNode;
  title?: string;
  stickyHeader?: boolean;
  breadcrumbProps?: BreadcrumbProps;
  onGoToCheckout?: () => Promise<void>;
  editBookingItemExtension?: (itemIDs: Array<string>) => void;
  className?: string;
  packageIncludedServices?: Array<ApiIncludedService>;
  packageModel?: ApiPackageModel;
  hideFooter?: boolean;
}

const BlankPageLayout = observer(function BlankPageLayout(props: Props) {
  const {
    children,
    stickyHeader,
    breadcrumbProps,
    onGoToCheckout,
    editBookingItemExtension,
    className,
    packageIncludedServices,
    packageModel,
    hideFooter
  } = props;

  const title = props.title ? `${props.title} | ${pageTitle}` : `${pageTitle}`;
  const query = useQuery();
  const workflow = QueryUtils.getWorkflowCode(query);
  const workflowType = QueryUtils.getWorkflowType(query);
  const isRebookingWorkflow = QueryUtils.getIsRebookingWorkflow(query);
  const isAddExtraWorkflow = QueryUtils.getIsAddExtraWorkflow(query);
  const cms = useCms();
  const [showHeader, setShowHeader] = useState<boolean>(false);

  useEffect(() => {
    if (
      extrasWorkflowPages.includes(location.pathname as PageUrl) ||
      singleHotelBookingPages.includes(location.pathname as PageUrl) ||
      b2bPages.includes(location.pathname as PageUrl) ||
      isAddExtraWorkflow
    ) {
      if (PageUrl.DASHBOARD === (location.pathname as PageUrl)) {
        setShowHeader(false);
      } else {
        setShowHeader(true);
      }
    } else if (
      innerManageBookingPages.includes(location.pathname as PageUrl) ||
      vouchersWorkflowPages.includes(location.pathname as PageUrl) ||
      isRebookingWorkflow
    ) {
      setShowHeader(false);
    } else if (workflow || workflowType) {
      if (workflow) {
        cms.fetchWorkflows(workflow).then(workflows => {
          setShowHeader(false);
          const packageWorkflow = workflows.find(
            item => item.type === WorkflowType.PackageWorkflow
          );
          setShowHeader(!!packageWorkflow);
        });
      } else if (workflowType) {
        if (PageUrl.CONFIRMATION == (location.pathname as PageUrl)) {
          if (WorkflowType.PackageWorkflow == workflowType) {
            setShowHeader(true);
          }
        }
      }
    } else {
      setShowHeader(false);
    }
  }, [workflow]);

  return (
    <div className={`blank-page-layout ${className}`}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
      </Helmet>
      <div className={'header'}>
        <Headroom disableInlineStyles pinStart={0} disable={!stickyHeader} className="mb-3">
          <div className="iso__header-container">
            <Header
              breadcrumbProps={breadcrumbProps}
              onGoToCheckout={onGoToCheckout}
              editBookingItemExtension={editBookingItemExtension}
              packageIncludedServices={packageIncludedServices}
              packageModel={packageModel}
              showHeader={showHeader}
              isAddExtraWorkflow={isAddExtraWorkflow}
            />
          </div>
        </Headroom>
      </div>
      <h1 className={'page-title'} role={'main'}>
        {title}
      </h1>
      <div className={`backgroundContainer container-fluid ${className}__wrapper`}>{children}</div>
      {hideFooter || !showHeader || isAddExtraWorkflow ? null : <Footer />}
    </div>
  );
});

export default BlankPageLayout;
